/**
 * The one standards merge (firm-model spec §3.2, R25): firm → deck, field by field.
 * Pure and es5-safe; the server resolves for prompts (the deck override rides the turn
 * context and the AI call body), and the plugin resolves locally for `set_text`'s
 * banned-word check, the purpose gate, the chart brand tier and the "This deck" view.
 *
 * - An absent (or blank) deck value inherits the firm's value.
 * - A field the firm disallows ignores the deck value (the deck keeps it; re-allowing
 *   restores it, R6) and reports `firm_locked`.
 * - Lists and brand records replace wholesale when allowed (R8): a deck that removes every
 *   firm banned word sends `[]`, which is an override, not a blank.
 */
import {
  STANDARDS_FIELDS,
  type AllowMask,
  type BrandFonts,
  type BrandPalette,
  type DeckStandardsOverride,
  type FirmStandards,
  type StandardsField,
} from "./standards.js";

export type StandardsSource = "firm" | "deck" | "firm_locked";

export type ResolvedStandards = {
  standards: FirmStandards;
  source: Record<StandardsField, StandardsSource>;
  /** Stable hash of (firm version, override revision, allow mask): joins a check's input
   * hash so a firm edit, a deck edit or a lock change marks standards findings stale. */
  resolvedVersion: string;
};

type ScalarField = "audiencePurpose" | "languageVariant" | "currencyFormat" | "calendarYearFormat" | "fiscalYearFormat" | "otherLabelRules";

const SCALARS: readonly ScalarField[] = [
  "audiencePurpose",
  "languageVariant",
  "currencyFormat",
  "calendarYearFormat",
  "fiscalYearFormat",
  "otherLabelRules",
];

/**
 * The mask that applies to a firm's decks (spec §6.2, A4): a firm that can't have a team
 * (a firm of one: not beta, not on a seat plan) always may override, so its stored locks
 * are ignored here and kept on record (re-enabling a team restores them). The panel hides
 * the toggles on the same test, so what it shows is what applies.
 */
export function effectiveAllowMask(allow: AllowMask, firmCanHaveTeam: boolean): AllowMask {
  return firmCanHaveTeam ? allow : {};
}

/** Whether a deck may override `field` (absent = allowed, R6). */
export function isFieldAllowed(allow: AllowMask, field: StandardsField): boolean {
  return allow[field] !== false;
}

function hasKeys(value: BrandPalette | BrandFonts | undefined): boolean {
  if (!value) return false;
  for (const key in value) {
    if (Object.prototype.hasOwnProperty.call(value, key) && (value as Record<string, unknown>)[key] !== undefined) {
      return true;
    }
  }
  return false;
}

/** Whether the deck override sets `field` at all. */
function deckSets(deck: FirmStandards, field: StandardsField): boolean {
  if (field === "bannedWords") return deck.bannedWords !== undefined;
  if (field === "brandPalette") return hasKeys(deck.brand?.palette);
  if (field === "brandFonts") return hasKeys(deck.brand?.fonts);
  const value = deck[field];
  // A prose field set blank is not an override; a structured one is set or absent.
  return typeof value === "string" ? value.trim() !== "" : value !== undefined && value !== null;
}

/** The result never aliases its inputs (a caller may edit it). */
function copyPalette(palette: BrandPalette): BrandPalette {
  const out: BrandPalette = { ...palette };
  if (palette.series) out.series = palette.series.slice();
  return out;
}

/** 32-bit FNV-1a, hex. Stable across runtimes (no locale, no Unicode surprises: the input
 * is ASCII built below). */
function fnv1a(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    // hash *= 16777619 (the FNV prime) in 32 bits, without es2015's Math.imul.
    hash = (hash + (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24)) >>> 0;
  }
  return ("0000000" + hash.toString(16)).slice(-8);
}

function resolvedVersionOf(firmVersion: number, overrideRevision: number, allow: AllowMask): string {
  let mask = "";
  for (let i = 0; i < STANDARDS_FIELDS.length; i++) mask += isFieldAllowed(allow, STANDARDS_FIELDS[i]) ? "1" : "0";
  return fnv1a(`f${firmVersion}.d${overrideRevision}.m${mask}`);
}

export function resolveStandards(
  firm: { standards: FirmStandards; version: number },
  deckOverride: DeckStandardsOverride | null,
  allow: AllowMask,
): ResolvedStandards {
  const deck = deckOverride ? deckOverride.standards : {};
  const source = {} as Record<StandardsField, StandardsSource>;
  const useDeck = (field: StandardsField): boolean => {
    if (!isFieldAllowed(allow, field)) {
      source[field] = "firm_locked";
      return false;
    }
    const fromDeck = deckSets(deck, field);
    source[field] = fromDeck ? "deck" : "firm";
    return fromDeck;
  };

  const out: FirmStandards = {};
  for (let i = 0; i < SCALARS.length; i++) {
    const field = SCALARS[i];
    const value = useDeck(field) ? deck[field] : firm.standards[field];
    // Structured values (currency, fiscal year) are records: copied, never aliased.
    if (value !== undefined) (out as Record<string, unknown>)[field] = typeof value === "object" && value !== null ? { ...value } : value;
  }
  const words = useDeck("bannedWords") ? deck.bannedWords : firm.standards.bannedWords;
  if (words !== undefined) out.bannedWords = words.slice();

  const palette = useDeck("brandPalette") ? deck.brand?.palette : firm.standards.brand?.palette;
  const fonts = useDeck("brandFonts") ? deck.brand?.fonts : firm.standards.brand?.fonts;
  if (palette || fonts) {
    out.brand = {};
    if (palette) out.brand.palette = copyPalette(palette);
    if (fonts) out.brand.fonts = { ...fonts };
  }

  return {
    standards: out,
    source,
    resolvedVersion: resolvedVersionOf(firm.version, deckOverride ? deckOverride.revision : 0, allow),
  };
}
